import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  FaChevronLeft, FaTimes, FaCheck, FaIdCard, FaUser, FaMapMarkerAlt, FaCamera,
  FaImage, FaLock, FaRedo, FaExclamationTriangle, FaCrosshairs, FaUpload, FaShieldAlt
} from "react-icons/fa";
import { generateWithVision, fileToDataUrl } from "../lib/pollinations";
import { supabase } from "../lib/supabase";

/* ═══════════════════════════════════════════════════════════════
   SHIRKAT FUND — KYC verification · Company-style verification 
   ═══════════════════════════════════════════════════════════════ */

const C = {
  bg: "#FFFFFF", // ⭐ PURE WHITE BACKGROUND
  card: "#FFFFFF", 
  field: "#FAF9F6",
  primary: "#589103",            /* ⭐ NEW GREEN BRAND COLOR */
  primaryHover: "#426e02",
  primaryLight: "rgba(88, 145, 3, 0.12)",
  text: "#1A1408", muted: "#6B5A2E", line: "#E5E7EB",
  danger: "#EF4444", ok: "#10B981",
};
const BRAND_GRADIENT = `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryHover} 100%)`;

const STAGES = ["intro", "details", "front", "back", "location", "review", "processing", "done"];
const SEGMENT = { details: 0, front: 1, back: 1, location: 2, review: 3 };
const STEP_LABELS = ["Details", "CNIC", "Location", "Review"];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const ease = [0.16, 1, 0.3, 1];

const fmtCnic = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 13);
  return [d.slice(0, 5), d.slice(5, 12), d.slice(12)].filter(Boolean).join("-");
};
const maskCnic = (v) => (v ? v.slice(0, 6) + "•••••-" + v.slice(-1) : "");

function validate(f) {
  const e = {};
  if (f.name.trim().length < 3) e.name = "Enter your full name as printed on your CNIC";
  if (f.father.trim().length < 3) e.father = "Enter your father's or husband's name";
  if (f.cnic.replace(/\D/g, "").length !== 13) e.cnic = "CNIC must be 13 digits";
  if (!f.dob) e.dob = "Select your date of birth";
  else {
    const age = (Date.now() - new Date(f.dob).getTime()) / (365.25 * 864e5);
    if (age < 18) e.dob = "You must be at least 18 years old";
    if (age > 100) e.dob = "Check your date of birth";
  }
  if (!/^3\d{9}$/.test(f.phone)) e.phone = "Enter a valid mobile number, e.g. 3001234567";
  if (f.city.trim().length < 2) e.city = "Enter your city";
  if (f.address.trim().length < 8) e.address = "Enter your full address";
  return e;
}

function cropToFrame(v, container, frame) {
  const cr = container.getBoundingClientRect(), fr = frame.getBoundingClientRect();
  const vw = v.videoWidth, vh = v.videoHeight;
  const s = Math.max(cr.width / vw, cr.height / vh);
  const ox = (cr.width - vw * s) / 2, oy = (cr.height - vh * s) / 2;
  const sx = (fr.left - cr.left - ox) / s, sy = (fr.top - cr.top - oy) / s;
  const sw = fr.width / s, sh = fr.height / s;
  const out = document.createElement("canvas");
  out.width = Math.min(1280, sw);
  out.height = (out.width * sh) / sw;
  out.getContext("2d").drawImage(v, sx, sy, sw, sh, 0, 0, out.width, out.height);
  return out.toDataURL("image/jpeg", 0.88);
}

/* ───────── UI atoms ───────── */
const Btn = ({ children, onClick, disabled, variant = "primary", className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`sk-btn ${variant === "primary" ? "sk-brand" : "sk-ghost"} flex h-14 w-full items-center justify-center gap-2.5 rounded-2xl text-[15.5px] font-semibold transition active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
  >
    {children}
  </button>
);

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="mb-2 block text-[13px] font-semibold tracking-wide" style={{ color: C.text }}>{label}</span>
    {children}
    {error && <span className="mt-1.5 flex items-center gap-1.5 text-[12px] font-medium" style={{ color: C.danger }}><FaExclamationTriangle size={10} />{error}</span>}
  </label>
);

const inputCls = "sk-input w-full rounded-2xl px-4 py-3.5 text-[15px] outline-none transition";
const inputStyle = (err) => ({ background: "#FFF", color: C.text, border: `1px solid ${err ? C.danger : C.line}` });

const Title = ({ children, sub }) => (
  <div className="mb-8">
    <h1 className="sk-display text-[34px] leading-[1.1] sm:text-[40px]" style={{ color: C.text }}>{children}</h1>
    {sub && <p className="mt-3 max-w-[420px] text-[15px] leading-relaxed" style={{ color: C.muted }}>{sub}</p>}
  </div>
);

const Notice = ({ tone = "danger", children }) => (
  <div className="mb-5 flex items-start gap-3 rounded-2xl p-4" style={tone === "danger" ? { background: "#FEF2F2", color: C.danger, border: "1px solid #FECACA" } : { background: C.primaryLight, color: C.text, border: `1px solid ${C.line}` }}>
    {tone === "danger" ? <FaExclamationTriangle className="mt-0.5 flex-shrink-0" /> : <FaLock className="mt-0.5 flex-shrink-0" style={{ color: C.primary }} />}
    <p className="text-[13.5px] font-medium leading-snug">{children}</p>
  </div>
);

function ScanLaser({ active = true }) {
  const reduce = useReducedMotion();
  if (!active) return null;
  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none absolute left-0 right-0 z-10"
      style={{ height: 72, marginTop: -72 }}
      initial={{ top: "8%" }}
      animate={reduce ? { top: "50%" } : { top: ["8%", "100%", "8%"] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
    >
      <div className="h-full w-full" style={{ background: "linear-gradient(to bottom, transparent, rgba(88, 145, 3, 0.30))" }} />
      <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: C.primary }} />
    </motion.div>
  );
}

/* ───────── Camera ───────── */
function Camera({ side, onCapture, onClose }) {
  const vRef = useRef(null), cRef = useRef(null), fRef = useRef(null), sRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState(null);
  const [count, setCount] = useState(3);
  const [flash, setFlash] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let dead = false;
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Camera needs a secure (HTTPS) connection. You can upload a photo below.");
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((s) => {
        if (dead) return s.getTracks().forEach((t) => t.stop());
        sRef.current = s;
        if (vRef.current) vRef.current.srcObject = s;
      })
      .catch(() => setErr("Camera blocked or unavailable. You can upload a photo below."));
    return () => { dead = true; sRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const snap = () => {
    if (flash || !vRef.current?.videoWidth) return;
    const url = cropToFrame(vRef.current, cRef.current, fRef.current);
    setFlash(true);
    setTimeout(() => onCapture(url), 260);
  };

  useEffect(() => {
    if (!ready || flash || err) return;
    if (count === 0) return snap();
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [ready, count, flash, err]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onCapture(await fileToDataUrl(f));
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black">
      <div ref={cRef} className="absolute inset-0 overflow-hidden">
        <video ref={vRef} autoPlay playsInline muted onLoadedMetadata={() => setReady(true)} className="h-full w-full object-cover" />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-5">
        <div ref={fRef} className="relative w-full max-w-[420px] overflow-hidden rounded-3xl" style={{ aspectRatio: "1.586 / 1", border: `1px solid ${C.primary}` }}>
          {["top-0 left-0 border-t-4 border-l-4 rounded-tl-3xl", "top-0 right-0 border-t-4 border-r-4 rounded-tr-3xl", "bottom-0 left-0 border-b-4 border-l-4 rounded-bl-3xl", "bottom-0 right-0 border-b-4 border-r-4 rounded-br-3xl"].map((c) => (
            <span key={c} className={`absolute h-10 w-10 ${c}`} style={{ borderColor: C.primary }} />
          ))}
          <div className="absolute inset-4 opacity-40" aria-hidden="true">
            <div className="absolute right-0 top-2 flex h-[52%] w-[30%] items-center justify-center rounded border border-dashed" style={{ borderColor: C.primary }}><FaUser style={{ color: C.primary }} /></div>
            {[0, 1, 2].map((i) => (<div key={i} className="absolute left-0 h-1.5 rounded" style={{ top: 12 + i * 22, width: `${52 - i * 8}%`, background: C.primary }} />))}
          </div>
          {ready && !err && <ScanLaser />}
        </div>
        <div className="mt-8 text-center">
          <p className="text-[12px] font-bold tracking-[0.18em]" style={{ color: C.primary }}>CNIC {side.toUpperCase()}</p>
          <h2 className="sk-display mt-1 text-[26px] text-white">{err ? "Camera unavailable" : !ready ? "Starting camera…" : `Hold steady${count > 0 ? " · " + count : ""}`}</h2>
          <p className="mx-auto mt-1.5 max-w-[320px] text-[13.5px] text-white/70">{err || "Fit the whole card inside the frame. Avoid glare and shadows."}</p>
        </div>
      </div>
      <button onClick={onClose} aria-label="Close camera" className="sk-btn absolute left-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md" style={{ top: "max(20px, env(safe-area-inset-top))" }}><FaTimes /></button>
      {err && (<div className="absolute bottom-10 left-1/2 w-[280px] -translate-x-1/2 text-center"><Btn onClick={() => fileInputRef.current?.click()}><FaUpload /> Upload photo instead</Btn><input type="file" accept="image/*" ref={fileInputRef} className="hidden" onChange={onFile} /></div>)}
      {!err && (<button onClick={snap} disabled={!ready} aria-label="Capture now" className="sk-btn absolute left-1/2 flex h-[76px] w-[76px] -translate-x-1/2 items-center justify-center rounded-full border-4 disabled:opacity-40" style={{ bottom: "max(28px, env(safe-area-inset-bottom))", borderColor: C.primary, background: "rgba(88, 145, 3, 0.22)" }}><span className="h-[54px] w-[54px] rounded-full" style={{ background: BRAND_GRADIENT }} /></button>)}
      <AnimatePresence>{flash && <motion.div initial={{ opacity: 0.9 }} animate={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-0 bg-white" />}</AnimatePresence>
    </motion.div>
  );
}

/* ───────── CNIC step ───────── */
function CardStep({ side, image, onSave }) {
  const [mode, setMode] = useState(image ? "result" : "choose");
  const [img, setImg] = useState(image);
  const [err, setErr] = useState(null);
  const [warn, setWarn] = useState(null);

  const verify = async (url) => {
    setImg(url); setErr(null); setWarn(null); setMode("checking");
    const started = Date.now();
    try {
      const pass1 = await generateWithVision({ imageUrl: url, prompt: `Is this a photo of an official ID card? If it is a selfie, a person, a poster, or a phone screenshot, reply 'NO'. Reply strictly 'YES' or 'NO'.`, system: "You are a strict KYC AI. Reject all non-ID images." });
      if (/no/i.test(String(pass1).trim())) { await sleep(Math.max(0, 1200 - (Date.now() - started))); setErr(`AI could not detect a valid CNIC. Please ensure it's a real ID card, not a poster or selfie.`); setImg(null); setMode("choose"); return; }
      const pass2 = await generateWithVision({ imageUrl: url, prompt: `Look at this image carefully. Does it have the official layout of a Pakistani CNIC? Specifically, can you see the words "Pakistan", "National Identity Card", or a 13-digit ID number formatted like XXXXX-XXXXXXX-X? Reply strictly 'YES' or 'NO'.`, system: "You are a strict OCR and layout verification AI. Look only for official CNIC text." });
      if (/no/i.test(String(pass2).trim())) { await sleep(Math.max(0, 1200 - (Date.now() - started))); setErr(`AI could not read the CNIC text. Please take a clearer photo.`); setImg(null); setMode("choose"); return; }
    } catch (e) { console.error(e); setWarn("Automatic check is unavailable right now. Your photo will be reviewed manually."); }
    await sleep(Math.max(0, 1500 - (Date.now() - started)));
    setMode("result");
  };

  const onFile = async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) verify(await fileToDataUrl(f)); };

  return (
    <>
      <Title sub={side === "front" ? "Take a clear photo of the front of your CNIC. Your photo and name must be readable." : "Now the back of your CNIC, where your address and validity dates are printed."}>{side === "front" ? "Front of your CNIC" : "Back of your CNIC"}</Title>
      {err && <Notice>{err}</Notice>}
      {mode === "choose" && (
        <div className="space-y-3">
          <div className="relative flex aspect-[1.586/1] items-center justify-center overflow-hidden rounded-3xl" style={{ background: "linear-gradient(145deg,#FFFFFF,#F8FBF2)", border: `1.5px dashed ${C.primary}` }}>
            <div className="absolute inset-5 opacity-60">
              <div className="absolute right-0 top-1 flex h-[56%] w-[28%] items-center justify-center rounded-xl" style={{ background: C.primaryLight }}><FaUser style={{ color: C.primary }} /></div>
              {[0, 1, 2, 3].map((i) => (<div key={i} className="absolute left-0 h-2 rounded-full" style={{ top: 8 + i * 20, width: `${54 - i * 8}%`, background: C.line }} />))}
            </div>
            <span className="relative flex h-16 w-16 items-center justify-center rounded-full" style={{ background: BRAND_GRADIENT, color: "#FFF" }}><FaIdCard size={26} /></span>
          </div>
          <Btn onClick={() => setMode("camera")}><FaCamera /> Open camera</Btn>
          <label className="sk-btn sk-ghost flex h-14 w-full cursor-pointer items-center justify-center gap-2.5 rounded-2xl text-[15px] font-semibold transition"><FaImage /> Upload from gallery<input type="file" accept="image/*" className="hidden" onChange={onFile} /></label>
        </div>
      )}
      {(mode === "checking" || mode === "result") && img && (
        <div>
          <div className="relative aspect-[1.586/1] overflow-hidden rounded-3xl bg-black" style={{ border: `2px solid ${mode === "result" ? C.ok : C.primary}` }}>
            <img src={img} alt={`CNIC ${side}`} className="h-full w-full object-cover" />
            {mode === "checking" && (<><div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.2)" }} /><ScanLaser /></>)}
            {mode === "result" && (<motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full" style={{ background: C.ok, color: "#FFF" }}><FaCheck /></motion.span>)}
          </div>
          <p className="mt-4 text-center text-[14px] font-semibold" style={{ color: mode === "checking" ? C.primaryHover : C.text }}>{mode === "checking" ? "Scanning your CNIC…" : "Photo captured and checked"}</p>
          {warn && <p className="mt-2 text-center text-[12.5px]" style={{ color: C.muted }}>{warn}</p>}
          {mode === "result" && (<div className="mt-6 space-y-3"><Btn onClick={() => onSave(img)}>Continue</Btn><Btn variant="ghost" onClick={() => { setImg(null); setMode("choose"); }}><FaRedo className="text-[13px]" /> Retake</Btn></div>)}
        </div>
      )}
      <AnimatePresence>{mode === "camera" && <Camera side={side} onClose={() => setMode("choose")} onCapture={verify} />}</AnimatePresence>
    </>
  );
}

/* ───────── Location step ───────── */
function LocationStep({ loc, setLoc, onNext }) {
  const [st, setSt] = useState(loc ? "ok" : "idle");
  const [err, setErr] = useState("");
  const locate = () => {
    if (!navigator.geolocation) { setErr("This device doesn't support location."); setSt("error"); return; }
    setSt("loading");
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const { latitude: lat, longitude: lon, accuracy } = p.coords;
        let addr = "";
        try { const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`, { headers: { Accept: "application/json" } }); addr = (await r.json()).display_name || ""; } catch (e) {}
        setLoc({ lat, lon, acc: Math.round(accuracy), addr }); setSt("ok");
      },
      (e) => { setErr(e.code === 1 ? "Location permission is blocked. Allow it in your browser's site settings, then try again." : "We couldn't get your location. Check GPS or your connection and try again."); setSt("error"); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };
  return (
    <>
      <Title sub="We confirm you're applying from your real location. This is used for verification only.">Confirm your location</Title>
      <div className="relative mx-auto mb-8 flex h-60 w-60 items-center justify-center">
        {[0, 1, 2].map((i) => (<motion.span key={i} className="absolute rounded-full" style={{ border: `1px solid ${C.primary}`, width: 84 + i * 56, height: 84 + i * 56, opacity: 0.4 - i * 0.1 }} animate={st === "loading" ? { scale: [0.85, 1.12, 0.85], opacity: [0.6, 0.1, 0.6] } : {}} transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }} />))}
        <span className="relative z-10 flex h-[84px] w-[84px] items-center justify-center rounded-full" style={{ background: st === "ok" ? C.ok : BRAND_GRADIENT, color: "#FFF" }}>{st === "ok" ? <FaCheck size={28} /> : <FaMapMarkerAlt size={30} />}</span>
      </div>
      {st === "ok" && loc && (<motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 rounded-2xl p-5" style={{ background: C.card, border: `1px solid ${C.line}` }}><p className="text-[12px] font-semibold" style={{ color: C.ok }}>Location confirmed</p><p className="mt-1.5 text-[14.5px] font-medium leading-snug">{loc.addr || "Address not available"}</p><p className="mt-2 text-[12px]" style={{ color: C.muted }}>{loc.lat.toFixed(5)}, {loc.lon.toFixed(5)} · accuracy ±{loc.acc} m</p></motion.div>)}
      {st === "error" && <Notice>{err}</Notice>}
      {st === "ok" ? (<div className="space-y-3"><Btn onClick={onNext}>Continue</Btn><Btn variant="ghost" onClick={locate}><FaRedo className="text-[13px]" /> Update location</Btn></div>) : (<Btn onClick={locate} disabled={st === "loading"}><FaCrosshairs /> {st === "loading" ? "Finding you…" : st === "error" ? "Try again" : "Share my location"}</Btn>)}
    </>
  );
}

/* ───────── Verification case panel (company-style) ───────── */
const CASE_STEPS = [
  ["details", "Applicant details", "Name, CNIC number and contact"],
  ["front", "CNIC front", "Identity document"],
  ["back", "CNIC back", "Address and validity dates"],
  ["location", "Location check", "Device location"],
  ["review", "Review and submit", "Final confirmation"],
];
function CompanyPanel({ stage, form, front, back, loc, refId, planAmount }) {
  const idx = STAGES.indexOf(stage);
  const state = (k) => { const i = STAGES.indexOf(k); return idx >= 6 || idx > i ? "done" : idx === i ? "active" : "todo"; };
  const rows = [
    ["Applicant", form.name || "Not entered"],
    ["CNIC", form.cnic ? maskCnic(form.cnic) : "Not entered"],
    ["Documents", `${[front, back].filter(Boolean).length} of 2 uploaded`],
    ["Location", loc ? "Confirmed" : "Pending"],
  ];
  return (
    <aside className="hidden flex-col justify-center border-l px-10 py-12 lg:flex lg:w-[44%] xl:w-[42%] xl:px-14" style={{ background: C.bg, borderColor: C.line }}>
      <div className="mx-auto w-full max-w-[420px]">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: BRAND_GRADIENT, color: "#FFF" }}><FaShieldAlt /></span>
          <div><p className="text-[17px] font-bold leading-tight">Shirkat Fund</p><p className="text-[12.5px]" style={{ color: C.muted }}>Identity verification</p></div>
        </div>

        <div className="overflow-hidden rounded-2xl" style={{ background: C.card, border: `1px solid ${C.line}` }}>
          <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: C.line }}>
            <div><p className="text-[12px] font-semibold" style={{ color: C.muted }}>Case reference</p><p className="mt-0.5 text-[15px] font-bold tracking-wider">{refId}</p></div>
            <span className="rounded-full px-3 py-1 text-[12px] font-bold" style={{ background: idx >= 6 ? "rgba(16,185,129,.12)" : C.primaryLight, color: idx >= 6 ? C.ok : C.primary }}>{idx >= 6 ? "Submitted" : "In progress"}</span>
          </div>

          <ol className="px-5 py-5">
            {CASE_STEPS.map(([k, t, d], i) => {
              const st = state(k);
              return (
                <li key={k} className="relative flex gap-4 pb-5 last:pb-0">
                  {i < CASE_STEPS.length - 1 && <span className="absolute left-[13px] top-7 h-[calc(100%-20px)] w-px" style={{ background: st === "done" ? C.primary : C.line }} />}
                  <span className="relative z-10 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-colors" style={st === "done" ? { background: C.primary, color: "#FFF" } : st === "active" ? { background: C.card, border: `2px solid ${C.primary}`, color: C.primary } : { background: C.card, border: `1.5px solid ${C.line}`, color: C.muted }}>
                    {st === "done" ? <FaCheck size={10} /> : i + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-[14px] font-semibold" style={{ color: st === "todo" ? C.muted : C.text }}>{t}</p>
                    <p className="text-[12.5px]" style={{ color: C.muted }}>{d}</p>
                  </div>
                  <span className="pt-0.5 text-[11.5px] font-semibold" style={{ color: st === "done" ? C.ok : st === "active" ? C.primary : C.muted }}>{st === "done" ? "Complete" : st === "active" ? "In progress" : "Pending"}</span>
                </li>
              );
            })}
          </ol>

          <div className="border-t px-5 py-4" style={{ borderColor: C.line, background: C.bg }}>
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-1.5 text-[13px]"><span style={{ color: C.muted }}>{k}</span><span className="max-w-[200px] truncate text-right font-semibold">{v}</span></div>
            ))}
            <div className="flex justify-between gap-4 py-1.5 text-[13px]"><span style={{ color: C.muted }}>Requested amount</span><span className="font-bold" style={{ color: C.primary }}>Rs {planAmount.toLocaleString()}</span></div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 text-[12.5px] font-medium">
          {[[FaLock, "256-bit encrypted"], [FaShieldAlt, "Data protected"]].map(([I, t]) => (
            <div key={t} className="flex items-center gap-2.5 rounded-xl px-4 py-3" style={{ background: C.card, border: `1px solid ${C.line}` }}><I style={{ color: C.primary }} />{t}</div>
          ))}
        </div>
        <p className="mt-4 text-[12px] leading-relaxed" style={{ color: C.muted }}>Documents are reviewed by our compliance team. You will be notified once your case is decided.</p>
      </div>
    </aside>
  );
}

/* ───────── Page ───────── */
export default function ApplyPage({ onSubmit }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planAmount = Number(params.get("amount")) || 40000;

  const [isAppLoading, setIsAppLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);

  const [stage, setStage] = useState("intro");
  const [form, setForm] = useState({ name: "", father: "", cnic: "", dob: "", gender: "male", phone: "", city: "", address: "" });
  const [errors, setErrors] = useState({});
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [loc, setLoc] = useState(null);
  const [consent, setConsent] = useState(false);
  const [prog, setProg] = useState(0);
  const [checks, setChecks] = useState(0);
  const [submitErr, setSubmitErr] = useState(null);
  const [refId] = useState(() => "SF-" + Date.now().toString(36).toUpperCase().slice(-5) + Math.random().toString(36).slice(2, 5).toUpperCase());

  const idx = STAGES.indexOf(stage);
  const go = (s) => setStage(s);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [stage]);

  useEffect(() => {
    if (!isAppLoading) return;
    const duration = 3000, intervalTime = 30, steps = duration / intervalTime;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      setLoadProgress(Math.min((currentStep / steps) * 100, 100));
      if (currentStep >= steps) { clearInterval(timer); setTimeout(() => setIsAppLoading(false), 200); }
    }, intervalTime);
    return () => clearInterval(timer);
  }, [isAppLoading]);

  const set = (k) => (e) => {
    const v = k === "cnic" ? fmtCnic(e.target.value) : k === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const submitDetails = () => { const e = validate(form); setErrors(e); if (!Object.keys(e).length) go("front"); };
  const back1 = () => (idx <= 1 ? navigate("/shirkat-fund") : go(STAGES[idx - 1]));

  const submit = async () => {
    setSubmitErr(null); setProg(0); setChecks(0); go("processing");
    try {
      for (let i = 1; i <= 3; i++) { await sleep(600); setChecks(i); setProg(i * 25); }

      const fileExt = "jpeg";
      const frontPath = `${refId}_front.${fileExt}`;
      const backPath = `${refId}_back.${fileExt}`;

      const base64ToBlob = (base64) => {
        const byteString = atob(base64.split(',')[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        return new Blob([ab], { type: 'image/jpeg' });
      };

      await supabase.storage.from('cnic-documents').upload(frontPath, base64ToBlob(front), { upsert: true });
      await supabase.storage.from('cnic-documents').upload(backPath, base64ToBlob(back), { upsert: true });

      const frontUrl = supabase.storage.from('cnic-documents').getPublicUrl(frontPath).data.publicUrl;
      const backUrl = supabase.storage.from('cnic-documents').getPublicUrl(backPath).data.publicUrl;

      const { data: { user } } = await supabase.auth.getUser();

      const { error: dbError } = await supabase
        .from('kyc_applications')
        .insert([{
          reference_id: refId,
          user_id: user?.id || null,
          plan_amount: planAmount,
          status: 'Review Pending',
          full_name: form.name,
          father_name: form.father,
          cnic: form.cnic,
          dob: form.dob,
          gender: form.gender,
          phone: "+92" + form.phone,
          city: form.city,
          address: form.address,
          location_address: loc?.addr || "",
          cnic_front_url: frontUrl,
          cnic_back_url: backUrl,
        }]);

      if (dbError) throw new Error(`Database Error: ${dbError.message}`);

      setChecks(4); setProg(100);
      await sleep(700);
      navigate(`/track-application`);
    } catch (e) {
      console.error(e);
      setSubmitErr(e.message || "Could not save to database.");
      go("review");
    }
  };

  const seg = SEGMENT[stage];
  const slide = { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 }, transition: { duration: 0.35, ease } };
  const amountChip = (
    <span className="rounded-full px-3.5 py-1.5 text-[12.5px] font-bold" style={{ color: C.primary, background: C.primaryLight, border: `1px solid ${C.line}` }}>Rs {planAmount.toLocaleString()}</span>
  );

  /* Loading screen */
  if (isAppLoading) {
    return (
      <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center" style={{ background: C.bg }}>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease }} className="relative mb-8 flex h-24 w-24 items-center justify-center rounded-full" style={{ background: C.primaryLight }}>
          <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.6, 0, 0.6] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="absolute inset-0 rounded-full" style={{ border: `2px solid ${C.primary}` }} />
          <FaShieldAlt size={38} style={{ color: C.primary }} />
        </motion.div>
        <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.5 }} className="mb-2 text-2xl font-bold" style={{ color: C.text }}>Securing your capital</motion.h1>
        <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3, duration: 0.5 }} className="mb-8 text-sm" style={{ color: C.muted }}>Preparing your KYC verification…</motion.p>
        <div className="h-1.5 w-64 overflow-hidden rounded-full" style={{ background: C.line }}>
          <motion.div className="h-full rounded-full" style={{ background: BRAND_GRADIENT }} initial={{ width: "0%" }} animate={{ width: `${loadProgress}%` }} transition={{ ease: "linear", duration: 0.1 }} />
        </div>
        <p className="mt-3 text-[11px] font-bold tracking-wider" style={{ color: C.primary }}>{Math.round(loadProgress)}%</p>
        <style>{FONT_CSS}</style>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ color: C.text, background: C.bg }}>
      {/* LEFT — FORM */}
      <div className="relative flex flex-1 flex-col px-5 pb-12 pt-6 sm:px-12 lg:px-16 xl:px-24" style={{ background: C.bg }}>
        {seg !== undefined ? (
          <header className="mx-auto flex w-full max-w-md items-center gap-3">
            <button onClick={back1} aria-label="Back" className="sk-btn -ml-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition hover:bg-white" style={{ color: C.text }}><FaChevronLeft /></button>
            <div className="flex-1">
              <div className="flex gap-1.5">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className="h-1 flex-1 overflow-hidden rounded-full" style={{ background: C.line }}>
                    <motion.span className="block h-full rounded-full" style={{ background: BRAND_GRADIENT }} initial={false} animate={{ width: i <= seg ? "100%" : "0%" }} transition={{ duration: 0.4 }} />
                  </span>
                ))}
              </div>
              <div className="mt-2 flex">{STEP_LABELS.map((l, i) => (<span key={l} className="flex-1 text-[11px] font-semibold" style={{ color: i <= seg ? C.primary : "rgba(107,90,46,.5)" }}>{l}</span>))}</div>
            </div>
            {amountChip}
          </header>
        ) : stage === "intro" ? (
          <header className="mx-auto flex w-full max-w-md items-center justify-between">
            <button onClick={() => navigate("/shirkat-fund")} aria-label="Close" className="sk-btn -ml-1 flex h-10 w-10 items-center justify-center rounded-full transition hover:bg-white" style={{ color: C.text }}><FaTimes /></button>
            {amountChip}
          </header>
        ) : null}

        <div className="mx-auto mt-4 flex w-full max-w-md items-center justify-between rounded-xl px-4 py-2.5 text-[12px] font-medium lg:hidden" style={{ background: C.card, border: `1px solid ${C.line}`, color: C.muted }}>
          <span className="flex items-center gap-2"><FaLock style={{ color: C.primary }} /> Secure session</span><span className="font-bold tracking-wider" style={{ color: C.text }}>{refId}</span>
        </div>

        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-8">
          <AnimatePresence mode="wait">
            <motion.div key={stage} {...slide} className="flex flex-col">

              {stage === "intro" && (
                <>
                  <Title sub={`Verify your identity to apply for Rs ${planAmount.toLocaleString()}. It takes about 2 minutes.`}>Let's verify your identity</Title>
                  <div className="space-y-3">
                    {[[FaUser, "Personal details", "Your name, CNIC number and contact"], [FaIdCard, "CNIC photos", "Front and back, scanned with your camera"], [FaMapMarkerAlt, "Location check", "Confirm where you are applying from"]].map(([I, t, s], i) => (
                      <div key={t} className="flex items-center gap-4 rounded-2xl p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}>
                        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: BRAND_GRADIENT, color: "#FFF" }}><I /></span>
                        <div className="flex-1"><p className="text-[15px] font-bold">{t}</p><p className="text-[13px]" style={{ color: C.muted }}>{s}</p></div>
                        <span className="flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold" style={{ background: C.primaryLight, color: C.primary }}>{i + 1}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5"><Notice tone="info">Your documents are encrypted and used only to verify your application.</Notice></div>
                  <Btn onClick={() => go("details")}>Start verification</Btn>
                </>
              )}

              {stage === "details" && (
                <>
                  <Title sub="Enter your details exactly as they appear on your CNIC.">Personal details</Title>
                  <div className="space-y-4">
                    <Field label="Full name" error={errors.name}><input className={inputCls} style={inputStyle(errors.name)} value={form.name} onChange={set("name")} placeholder="e.g. Ahmed Khan" autoComplete="name" /></Field>
                    <Field label="Father's / husband's name" error={errors.father}><input className={inputCls} style={inputStyle(errors.father)} value={form.father} onChange={set("father")} /></Field>
                    <Field label="CNIC number" error={errors.cnic}><input className={inputCls} style={inputStyle(errors.cnic)} value={form.cnic} onChange={set("cnic")} inputMode="numeric" placeholder="12345-1234567-1" /></Field>
                    <Field label="Date of birth" error={errors.dob}><input type="date" className={inputCls} style={inputStyle(errors.dob)} value={form.dob} onChange={set("dob")} max={new Date().toISOString().slice(0, 10)} /></Field>
                    <Field label="Gender"><div className="flex gap-2 rounded-2xl p-1" style={{ background: C.field, border: `1px solid ${C.line}` }}>{["male", "female", "other"].map((g) => (<button key={g} type="button" onClick={() => setForm((f) => ({ ...f, gender: g }))} className="sk-btn flex-1 rounded-xl py-2.5 text-[14px] font-semibold capitalize transition" style={form.gender === g ? { background: BRAND_GRADIENT, color: "#FFF" } : { color: C.muted }}>{g}</button>))}</div></Field>
                    <Field label="Mobile number" error={errors.phone}><div className="flex gap-2"><span className="flex items-center rounded-2xl px-4 text-[15px] font-semibold" style={{ background: C.field, border: `1px solid ${C.line}` }}>+92</span><input className={inputCls} style={inputStyle(errors.phone)} value={form.phone} onChange={set("phone")} inputMode="numeric" placeholder="3001234567" autoComplete="tel-national" /></div></Field>
                    <Field label="City" error={errors.city}><input className={inputCls} style={inputStyle(errors.city)} value={form.city} onChange={set("city")} /></Field>
                    <Field label="Full address" error={errors.address}><textarea rows={2} className={inputCls} style={inputStyle(errors.address)} value={form.address} onChange={set("address")} /></Field>
                  </div>
                  <div className="mt-8"><Btn onClick={submitDetails}>Continue</Btn></div>
                </>
              )}

              {stage === "front" && <CardStep side="front" image={front} onSave={(i) => { setFront(i); go("back"); }} />}
              {stage === "back" && <CardStep side="back" image={back} onSave={(i) => { setBack(i); go("location"); }} />}
              {stage === "location" && <LocationStep loc={loc} setLoc={setLoc} onNext={() => go("review")} />}

              {stage === "review" && (
                <>
                  <Title sub="Check everything is correct before you submit.">Review and submit</Title>
                  <div className="space-y-3">
                    <div className="rounded-2xl px-5 py-2" style={{ background: C.card, border: `1px solid ${C.line}` }}>
                      {[["Name", form.name], ["Father / husband", form.father], ["CNIC", maskCnic(form.cnic)], ["Date of birth", form.dob], ["Mobile", "+92 " + form.phone], ["City", form.city]].map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-4 border-b py-3 text-[13.5px] last:border-0" style={{ borderColor: C.line }}><span style={{ color: C.muted }}>{k}</span><span className="text-right font-semibold">{v}</span></div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[["Front", front], ["Back", back]].map(([l, s]) => (
                        <div key={l} className="overflow-hidden rounded-2xl bg-black" style={{ border: `1px solid ${C.line}` }}><img src={s} alt={`CNIC ${l}`} className="aspect-[1.586/1] w-full object-cover" /><p className="bg-white py-2 text-center text-[12px] font-semibold" style={{ color: C.muted }}>{l}</p></div>
                      ))}
                    </div>
                    <div className="flex items-start gap-3 rounded-2xl p-4" style={{ background: C.card, border: `1px solid ${C.line}` }}><FaMapMarkerAlt className="mt-1 flex-shrink-0" style={{ color: C.primary }} /><p className="text-[13px] font-medium leading-snug">{loc?.addr || `${loc?.lat.toFixed(4)}, ${loc?.lon.toFixed(4)}`}</p></div>
                  </div>
                  <label className="mt-5 flex cursor-pointer items-start gap-3 text-[13px] leading-relaxed" style={{ color: C.muted }}><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="sr-only" /><span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md transition-colors" style={{ background: consent ? C.primary : "#FFF", border: `1.5px solid ${consent ? C.primary : C.muted}`, color: consent ? "#FFF" : "#FFF" }}>{consent && <FaCheck className="text-[10px]" />}</span>I confirm these details and documents are mine, and I agree to their use for identity verification.</label>
                  {submitErr && <p className="mt-3 text-[13px] font-medium" style={{ color: C.danger }}>{submitErr}</p>}
                  <div className="mt-8"><Btn onClick={submit} disabled={!consent}>Submit for verification</Btn></div>
                </>
              )}

              {stage === "processing" && (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="relative mb-9 flex h-48 w-48 items-center justify-center">
                    <svg viewBox="0 0 120 120" className="absolute inset-0 -rotate-90">
<defs><linearGradient id="skg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={C.primary} /><stop offset="100%" stopColor={C.primaryHover} /></linearGradient></defs>                      <circle cx="60" cy="60" r="52" fill="none" stroke={C.line} strokeWidth="6" />
                      <circle cx="60" cy="60" r="52" fill="none" stroke="url(#skg)" strokeWidth="6" strokeLinecap="round" strokeDasharray={2 * Math.PI * 52} strokeDashoffset={2 * Math.PI * 52 * (1 - prog / 100)} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
                    </svg>
                    <span className="sk-display text-[44px]" style={{ color: C.primary }}>{prog}%</span>
                  </div>
                  <h1 className="sk-display mb-7 text-[30px]">Verifying your application</h1>
                  <div className="w-full max-w-[320px] space-y-3.5 text-left">
                    {["Validating your details", "Securing CNIC photos", "Confirming location", "Submitting application"].map((t, i) => (
                      <div key={t} className="flex items-center gap-3 text-[14.5px]" style={{ color: i < checks ? C.text : C.muted }}><span className="flex h-6 w-6 items-center justify-center rounded-full transition-colors" style={{ background: i < checks ? C.ok : "transparent", border: `1.5px solid ${i < checks ? C.ok : C.line}`, color: "#FFF" }}>{i < checks && <FaCheck className="text-[10px]" />}</span>{t}</div>
                    ))}
                  </div>
                </div>
              )}

              {stage === "done" && (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="relative mb-8 flex h-40 w-40 items-center justify-center">
                    <motion.span className="absolute inset-0 rounded-full" style={{ border: `2px solid ${C.primary}` }} initial={{ scale: 0.8, opacity: 0.7 }} animate={{ scale: 1.5, opacity: 0 }} transition={{ duration: 1.8, repeat: Infinity }} />
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 16 }} className="flex h-32 w-32 items-center justify-center rounded-full" style={{ background: BRAND_GRADIENT }}><svg viewBox="0 0 52 52" className="h-16 w-16"><motion.path d="M14 27 L23 36 L39 17" fill="none" stroke="#FFF" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.3, duration: 0.5 }} /></svg></motion.div>
                  </div>
                  <h1 className="sk-display text-[34px]">Verification complete</h1>
                  <p className="mt-3 max-w-[340px] text-[14.5px] leading-relaxed" style={{ color: C.muted }}>Your details, CNIC and location are submitted. We're reviewing your application for Rs {planAmount.toLocaleString()} and will notify you on +92 {form.phone}.</p>
                  <div className="mt-6 rounded-2xl px-5 py-3 text-[13px]" style={{ background: C.card, border: `1px solid ${C.line}` }}><span style={{ color: C.muted }}>Reference </span><span className="font-bold tracking-wider">{refId}</span></div>
                  <div className="w-full pt-10"><Btn onClick={() => navigate("/shirkat-fund")}>Back to Shirkat Fund</Btn></div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* RIGHT — SHOWCASE */}
      <CompanyPanel stage={stage} form={form} front={front} back={back} loc={loc} refId={refId} planAmount={planAmount} />

      <style>{FONT_CSS}</style>
    </div>
  );
}

const FONT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
* { font-family: 'Inter', system-ui, sans-serif; }
.sk-display, .sk-display * { font-weight: 700; letter-spacing: -0.02em; }
.sk-btn:focus-visible { outline: 2px solid ${C.primary}; outline-offset: 3px; }
.sk-brand { background: ${BRAND_GRADIENT}; color: #FFF; }
.sk-brand:hover:not(:disabled) { filter: brightness(1.06); transform: translateY(-1px); }
.sk-ghost { background: #fff; color: ${C.text}; border: 1px solid ${C.line}; }
.sk-ghost:hover { background: ${C.field}; }
.sk-input:hover { border-color: rgba(88, 145, 3, 0.55) !important; }
.sk-input:focus { border-color: ${C.primary} !important; box-shadow: 0 0 0 4px rgba(88, 145, 3, 0.20); }
.sk-input::placeholder { color: #9CA3AF; }
@media (prefers-reduced-motion: reduce) { .sk-brand:hover:not(:disabled) { transform: none; } }
`;